import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import {
    RouterProvider,
    createBrowserHistory,
    createRouter,
} from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { PostHogProvider } from 'posthog-js/react'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { App } from '@capacitor/app'
import { useSharedProductStore } from '@/stores/sharedProductStore'
import './index.css'

// Import the generated route tree
import { routeTree } from './routeTree.gen'
// import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";

// Import validated environment configuration
import { env } from '@/config/env'

// Import Sentry configuration (MUST be before React renders)
import * as Sentry from '@sentry/react'
import {
    initializeSentry,
    captureException,
    addBreadcrumb,
} from '@/config/sentry'
import { SentryErrorBoundary } from '@/components/errors/ErrorBoundary'

const { logger } = Sentry

// Import API client configuration
import {
    configureApiClient,
    configureAuth,
} from '@wtf/shared/api/utils/api-client'
import { getIdToken, waitForAuthInit } from '@/auth/firebase'

// Initialize Sentry as early as possible
initializeSentry()

// Configure status bar for edge-to-edge display on native platforms
if (Capacitor.isNativePlatform()) {
    // Enable edge-to-edge mode (WebView extends behind status bar)
    StatusBar.setOverlaysWebView({ overlay: true })
    // Set status bar style (Light = dark icons for light backgrounds)
    StatusBar.setStyle({ style: Style.Light })

    // For Android: Get actual status bar height and set as CSS variable fallback
    if (Capacitor.getPlatform() === 'android') {
        StatusBar.getInfo().then((info) => {
            // height property exists on Android but may not be in TypeScript types
            if ('height' in info && typeof (info as any).height === 'number') {
                document.documentElement.style.setProperty(
                    '--android-status-bar-height',
                    `${(info as any).height}px`
                )
            }
        })
    }

    // Listen for URL opens from Share Extension
    App.addListener('appUrlOpen', ({ url }) => {
        logger.info('[App] URL opened:', { url })
        handleIncomingUrl(url)
    })

    // Also check if app was launched with a URL (cold start)
    App.getLaunchUrl().then((result) => {
        if (result?.url) {
            logger.info('[App] Launched with URL:', { url: result.url })
            handleIncomingUrl(result.url)
        }
    })
}

// Handle incoming URL from share extension
function handleIncomingUrl(url: string) {
    // Parse the URL scheme: wtfyouarewearing://share?url={encoded_url}
    try {
        // Try standard URL parsing first
        const urlObj = new URL(url)
        if (urlObj.protocol === 'wtfyouarewearing:' && urlObj.hostname === 'share') {
            const sharedUrl = urlObj.searchParams.get('url')
            if (sharedUrl) {
                const decodedUrl = decodeURIComponent(sharedUrl)
                logger.info('[App] Shared product URL:', { decodedUrl })

                // Store the shared URL in state
                useSharedProductStore.getState().setSharedUrl(decodedUrl)
                return
            }
        }
    } catch (urlError) {
        // Fallback to manual parsing for malformed custom scheme URLs
        // (some iOS versions don't parse custom schemes correctly)
        try {
            if (url.startsWith('wtfyouarewearing://share')) {
                // Extract the query string manually since URL parsing can be unreliable for custom schemes
                const queryStart = url.indexOf('?')
                if (queryStart !== -1) {
                    const queryString = url.substring(queryStart + 1)
                    const params = new URLSearchParams(queryString)
                    const sharedUrl = params.get('url')

                    if (sharedUrl) {
                        const decodedUrl = decodeURIComponent(sharedUrl)
                        logger.info('[App] Shared product URL:', { decodedUrl })

                        // Store the shared URL in state
                        useSharedProductStore.getState().setSharedUrl(decodedUrl)
                        return
                    }
                }
            }
        } catch (error) {
            logger.error('[App] Error parsing URL:', { error })
        }
    }
}

// Configure shared API client with Sentry integration
configureApiClient({
    captureException,
    addBreadcrumb,
})

// Configure shared API client with Firebase auth
configureAuth({
    getIdToken,
    waitForAuthInit,
})

// Create a QueryClient instance with global defaults
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Disable refetch on window focus globally to prevent 429 errors
            // when returning to the app after extended periods away.
            // Individual queries can opt-in with refetchOnWindowFocus: true if needed.
            refetchOnWindowFocus: false,
        },
    },
})

// Create a new router instance with the queryClient in context
const router = createRouter({
    routeTree,
    history: createBrowserHistory(),
    basepath: import.meta.env.BASE_URL,
    defaultPreload: 'intent',
    scrollRestorationBehavior: 'instant',
    scrollRestoration: true,
    context: {
        queryClient,
    },
    getScrollRestorationKey: (location) => {
        const paths = ['/']
        const productPagePattern = /^\/products\//

        if (paths.includes(location.pathname)) {
            // Home page - include tab parameter to maintain separate scroll states
            const searchParams = new URLSearchParams(location.search)
            const tab = searchParams.get('tab') || 'following'
            return `${location.pathname}?tab=${tab}`
        } else if (productPagePattern.test(location.pathname)) {
            return location.pathname // Product pages use full path as key
        } else {
            return location.state.key! // Other pages use state key
        }
    },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router
    }
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement)

    // Only enable PostHog in production environment (validated at build time)
    const enablePostHog = env.ENVIRONMENT === 'production'

    const app = (
        <SentryErrorBoundary>
            <HelmetProvider>
                <QueryClientProvider client={queryClient}>
                    <RouterProvider router={router} />
                    {/* <TanStackRouterDevtools router={router} initialIsOpen={false} position="top-left" /> */}
                </QueryClientProvider>
            </HelmetProvider>
        </SentryErrorBoundary>
    )

    root.render(
        <StrictMode>
            {enablePostHog && env.ENVIRONMENT === 'production' ? (
                <PostHogProvider
                    apiKey={env.POSTHOG_KEY}
                    options={{
                        api_host: env.POSTHOG_HOST,
                        defaults: '2025-05-24',
                        capture_exceptions: true,
                        debug: false,
                    }}
                >
                    {app}
                </PostHogProvider>
            ) : (
                app
            )}
        </StrictMode>
    )
}
